/**
 * Frontend mirror of api/constants/messageCodes.mjs.
 *
 * Wire format: 6-digit number `<KIND><DOMAIN><SEQ>`
 *   - KIND   (1):  1 = error, 2 = success
 *   - DOMAIN (2):  01 auth, 02 couple, 03 events, 04 guests, 05 rsvp,
 *                  06 gallery, 07 uploads, 08 albums, 09 billing,
 *                  10 admin, 11 contact/newsletter, 12 webhooks,
 *                  13 resource interactions (likes/comments),
 *                  14 wishes (video/text wishes — Pro feature),
 *                  15 quiz (couples build, guests play — Pro feature),
 *                  16 seating (reception table planning — Pro feature),
 *                  17 organizer (planning hub — Heirloom feature),
 *                  18 sandbox ("Try the full plan" demo),
 *                  19 AI assistant (Agapaé AI — Heirloom feature),
 *                  90 validation, 91 rate-limit/quota, 99 generic
 *   - SEQ    (3):  001..999 within the domain+kind bucket
 *
 * Keep in sync with the backend registry — CI verifies parity.
 */
export const CODES = {
  // ── Auth & sessions ─────────────────────────────────────────────────
  AUTH_REFRESH_TOKEN_REQUIRED: 101001,
  AUTH_REFRESH_TOKEN_INVALID: 101002,
  AUTH_TOKEN_TYPE_INVALID: 101003,
  AUTH_COUPLE_NOT_FOUND: 101004,
  AUTH_SESSION_EXPIRED_PASSWORD_CHANGED: 101005,
  AUTH_REFRESH_FAILED: 101006,
  AUTH_ADMIN_NOT_FOUND: 101007,
  AUTH_INVALID_CREDENTIALS: 101010,
  AUTH_ACCOUNT_INACTIVE: 101011,
  AUTH_LOGIN_FAILED: 101012,

  // ── Couple account (domain 02) ──────────────────────────────────────
  COUPLE_EMAIL_REQUIRED: 102001,
  COUPLE_RESET_TOKEN_REQUIRED: 102002,
  COUPLE_RESET_TOKEN_INVALID: 102003,
  COUPLE_EMAIL_ALREADY_REGISTERED: 102004,
  // 102005 (COUPLE_EVENT_HAS_OWNER) retired with /api/couple/register (F-PAY-04).
  COUPLE_VERIFICATION_CODE_REQUIRED: 102006,
  COUPLE_VERIFICATION_CODE_INVALID: 102007,
  COUPLE_VERIFICATION_CODE_EXPIRED: 102008,
  COUPLE_NOT_FOUND: 102009,
  COUPLE_EVENT_NOT_FOUND: 102010,
  COUPLE_DATE_CHANGE_BLOCKED: 102011,
  COUPLE_JOIN_CODE_LENGTH_INVALID: 102012,
  COUPLE_JOIN_CODE_CHARS_INVALID: 102013,
  COUPLE_RSVP_DEADLINE_INVALID: 102014,
  COUPLE_RSVP_DEADLINE_AFTER_WEDDING: 102015,
  COUPLE_INCORRECT_CURRENT_PASSWORD: 102016,
  COUPLE_PLAN_DOWNGRADE_BLOCKED: 102017,
  COUPLE_INVALID_PLAN_CONFIG: 102018,
  COUPLE_STRIPE_PRICE_NOT_CONFIGURED: 102019,
  COUPLE_EMAIL_VERIFICATION_REQUIRED: 102020,
  COUPLE_AUTH_REQUIRED: 102021,
  COUPLE_AUTH_TOKEN_INVALID: 102022,
  COUPLE_BATCH_DELETE_KEYS_REQUIRED: 102023,
  COUPLE_BATCH_DELETE_KEY_OWNERSHIP: 102024,
  COUPLE_NO_EVENT_ON_ACCOUNT: 102025,
  COUPLE_DELETE_PASSWORD_REQUIRED: 102026,
  COUPLE_DELETE_PASSWORD_INVALID: 102027,
  COUPLE_DELETE_FAILED: 102028,
  COUPLE_VERIFICATION_CODE_BURNED: 102029,
  // Captcha codes are shared by every Turnstile-protected route (signup,
  // contact, newsletter, …). Numeric values stay in the couple bucket for
  // historical reasons — the constant name is neutral.
  TURNSTILE_REQUIRED: 102030,
  TURNSTILE_INVALID: 102031,
  COUPLE_GIFT_IBAN_INVALID: 102032,
  // Invitation photo customization (couples can replace default invite photos)
  COUPLE_INVITATION_PHOTO_INVALID_SLOT: 102040,
  COUPLE_INVITATION_PHOTO_FILE_REQUIRED: 102041,
  COUPLE_INVITATION_PHOTO_FILE_TOO_LARGE: 102042,
  COUPLE_INVITATION_PHOTO_UNSUPPORTED_FORMAT: 102043,
  COUPLE_INVITATION_PHOTO_UPLOAD_FAILED: 102044,
  // Printable QR card: a couple's own saved templates
  COUPLE_QR_TEMPLATE_NOT_FOUND: 102045,
  COUPLE_QR_TEMPLATE_LIMIT_REACHED: 102046,

  // ── Couple account: success (domain 02, kind 2) ────────────────────
  COUPLE_INVITATION_PHOTO_UPLOADED: 202001,
  COUPLE_INVITATION_PHOTO_RESTORED: 202002,
  COUPLE_LANGUAGE_UPDATED: 202003,
  COUPLE_QR_CARD_SAVED: 202004,
  COUPLE_QR_TEMPLATE_SAVED: 202005,
  COUPLE_QR_TEMPLATE_RENAMED: 202006,
  COUPLE_QR_TEMPLATE_DELETED: 202007,
  COUPLE_QR_TEMPLATE_UPDATED: 202008,
  COUPLE_QR_TEMPLATE_RESET: 202009,
  COUPLE_INVITATION_SAVED: 202010,

  // ── Events (domain 03) ──────────────────────────────────────────────
  EVENTS_NOT_FOUND: 103001,
  EVENTS_NOT_READY: 103002,
  EVENTS_INVALID_JOIN_CODE: 103003,
  EVENTS_EMAIL_REQUIRED: 103004,
  EVENTS_FETCH_FAILED: 103005,
  EVENTS_EVENT_MISMATCH: 103006,

  // ── Guests & invitations (domain 04) ────────────────────────────────
  GUESTS_EVENT_ID_MISMATCH: 104001,
  GUESTS_EVENT_NOT_FOUND: 104002,
  GUESTS_GUEST_NOT_FOUND: 104003,
  GUESTS_PARTY_NOT_FOUND: 104004,
  GUESTS_PARTY_MEMBER_NOT_FOUND: 104005,
  GUESTS_GUEST_NOT_PENDING_APPROVAL: 104006,
  GUESTS_MEMBER_NOT_PENDING_REVIEW: 104007,
  GUESTS_CANNOT_REJECT_PRIMARY_MEMBER: 104008,
  GUESTS_INVITATION_LOCKED_RESPONDED: 104009,
  GUESTS_PARTY_NEEDS_ONE_PRIMARY: 104010,
  GUESTS_ONLY_DRAFT_DELETABLE: 104011,
  GUESTS_BULK_IDS_REQUIRED: 104012,
  GUESTS_LIMIT_REACHED: 104013,

  // ── RSVP (domain 05) ────────────────────────────────────────────────
  RSVP_GUEST_NOT_FOUND: 105001,
  RSVP_ALREADY_SUBMITTED: 105002,
  RSVP_INVALID_REQUEST_DATA: 105003,
  RSVP_INVALID_SUBMISSION_DATA: 105004,
  RSVP_RATE_LIMITED: 105005,

  // ── Gallery & media (domain 06) ─────────────────────────────────────
  GALLERY_EVENT_NOT_FOUND: 106001,
  GALLERY_DELETE_KEY_REQUIRED: 106002,
  GALLERY_EVENT_MISMATCH: 106003,
  GALLERY_INVALID_MEDIA_ID: 106004,
  GALLERY_MEDIA_NOT_FOUND: 106005,
  GALLERY_BATCH_DELETE_KEYS_REQUIRED: 106006,
  GALLERY_PRIVATE: 106007,
  GALLERY_NO_MEDIA: 106008,
  GALLERY_LOAD_FAILED: 106009,
  GALLERY_DASHBOARD_FAILED: 106010,
  GALLERY_DELETE_FAILED: 106011,
  GALLERY_BATCH_DELETE_FAILED: 106012,
  GALLERY_EXPORT_FAILED: 106013,
  GALLERY_EXPORT_TOO_MANY_FILES: 106014,
  GALLERY_EXPORT_TOO_LARGE: 106015,
  GALLERY_EXPORT_TOO_MANY_PARTS: 106016,
  GALLERY_EXPORT_PLAN_UNAVAILABLE: 106017,
  GALLERY_EXPORT_NOT_READY: 106018,

  // ── Uploads (domain 07) ─────────────────────────────────────────────
  UPLOADS_EVENT_MISMATCH: 107001,
  UPLOADS_FILE_TOO_LARGE_IMAGE: 107002,
  UPLOADS_FILE_TOO_LARGE_VIDEO: 107003,
  UPLOADS_QUOTA_EXCEEDED: 107004,
  UPLOADS_INSUFFICIENT_STORAGE: 107005,
  UPLOADS_KEY_OWNERSHIP: 107006,
  UPLOADS_START_FAILED: 107007,
  UPLOADS_SIGN_PART_FAILED: 107008,
  UPLOADS_COMPLETE_FAILED: 107009,
  UPLOADS_ABORT_FAILED: 107010,
  UPLOADS_RATE_LIMIT: 107011,
  UPLOADS_STATUS_FAILED: 107012,
  UPLOADS_INVALID_CONTENT: 107013,
  UPLOADS_VIDEO_NOT_IN_PLAN: 107014,
  UPLOADS_PHOTO_LIMIT_REACHED: 107015,

  // ── Albums (domain 08) ──────────────────────────────────────────────
  ALBUMS_NOT_FOUND: 108001,
  ALBUMS_EVENT_MISMATCH: 108002,
  ALBUMS_NOT_AUTHORIZED: 108003,
  ALBUMS_INVALID_ID_FORMAT: 108004,
  ALBUMS_NO_VALID_MEDIA: 108005,
  ALBUMS_DOWNLOAD_NOT_ALLOWED: 108006,
  ALBUMS_NO_PHOTOS_TO_DOWNLOAD: 108007,
  ALBUMS_MEDIA_NOT_FOUND: 108008,
  ALBUMS_MEDIA_NOT_IN_ALBUM: 108009,
  ALBUMS_SOURCE_NOT_FOUND: 108010,
  ALBUMS_TARGET_NOT_FOUND: 108011,
  ALBUMS_DIFFERENT_EVENT: 108012,
  ALBUMS_MEDIA_IDS_REQUIRED: 108013,
  ALBUMS_NO_PHOTOS_IN_COLLECTION: 108014,
  ALBUMS_EVENT_NOT_FOUND: 108015,
  ALBUMS_LOAD_FAILED: 108016,
  ALBUMS_CREATE_FAILED: 108017,
  ALBUMS_UPDATE_FAILED: 108018,
  ALBUMS_DELETE_FAILED: 108019,
  ALBUMS_LOAD_PHOTOS_FAILED: 108020,
  ALBUMS_ADD_PHOTOS_FAILED: 108021,
  ALBUMS_REMOVE_PHOTO_FAILED: 108022,
  ALBUMS_MOVE_PHOTO_FAILED: 108023,
  ALBUMS_EXPORT_FAILED: 108024,
  ALBUMS_ZIP_FAILED: 108025,
  ALBUMS_DOWNLOAD_FAILED: 108026,

  // ── Billing & subscriptions (domain 09) ─────────────────────────────
  BILLING_FETCH_PLANS_FAILED: 109001,
  BILLING_FETCH_ADDONS_FAILED: 109002,
  BILLING_ACCESS_DENIED: 109003,
  BILLING_EVENT_NOT_FOUND: 109004,
  BILLING_FETCH_SUBSCRIPTION_FAILED: 109005,
  BILLING_INVALID_PLAN: 109006,
  BILLING_CHECKOUT_FAILED: 109007,
  // 109008–109011 retired with /api/billing/create-checkout (F-PAY-04).
  BILLING_NO_ACTIVE_SUBSCRIPTION: 109012,
  BILLING_PORTAL_FAILED: 109013,
  BILLING_CANCEL_FAILED: 109014,
  BILLING_NO_SUB_TO_REACTIVATE: 109015,
  BILLING_REACTIVATE_FAILED: 109016,
  BILLING_INVALID_ADDON: 109017,
  BILLING_ADDON_ADD_FAILED: 109018,
  BILLING_ADDON_NOT_FOUND: 109019,
  BILLING_ADDON_REMOVE_FAILED: 109020,
  // 109021–109025 retired with the /verify-payment route (F-PAY-05).
  BILLING_FETCH_SUBSCRIPTIONS_FAILED: 109026,

  // ── Admin (domain 10) ───────────────────────────────────────────────
  ADMIN_CREDENTIALS_REQUIRED: 110001,
  ADMIN_INVALID_CREDENTIALS: 110002,
  ADMIN_LOGIN_FAILED: 110003,
  ADMIN_REGISTRATION_FIELDS_REQUIRED: 110004,
  ADMIN_PASSWORD_TOO_SHORT: 110005,
  ADMIN_EMAIL_ALREADY_REGISTERED: 110006,
  ADMIN_INVALID_PLAN: 110007,
  ADMIN_REGISTRATION_FAILED: 110008,
  ADMIN_EVENT_ID_EXISTS: 110009,
  ADMIN_CODE_REQUIRED: 110010,
  ADMIN_EVENT_NOT_FOUND: 110011,
  ADMIN_BATCH_DELETE_KEYS_REQUIRED: 110012,
  ADMIN_BATCH_DELETE_FAILED: 110013,
  ADMIN_NO_MEDIA_TO_EXPORT: 110014,
  ADMIN_EXPORT_ZIP_FAILED: 110015,
  ADMIN_RETENTION_SET_FAILED: 110016,
  ADMIN_RETENTION_GET_FAILED: 110017,
  ADMIN_EVENT_ID_REQUIRED: 110018,

  // ── Contact / newsletter (domain 11) ────────────────────────────────
  CONTACT_RATE_LIMITED: 111001,
  NEWSLETTER_RATE_LIMITED: 111002,

  // ── Webhooks (domain 12) ────────────────────────────────────────────
  WEBHOOKS_SECRET_NOT_CONFIGURED: 112001,
  WEBHOOKS_SIGNATURE_INVALID: 112002,

  // ── Resource interactions: likes & comments (domain 13) ─────────────
  RESOURCE_MEDIA_NOT_FOUND: 113001,
  RESOURCE_EVENT_MISMATCH: 113002,
  RESOURCE_LIKE_FAILED: 113003,
  RESOURCE_UNLIKE_FAILED: 113004,
  RESOURCE_INTERACTIONS_DISABLED: 113005,
  RESOURCE_COMMENT_BODY_REQUIRED: 113010,
  RESOURCE_COMMENT_BODY_TOO_LONG: 113011,
  RESOURCE_PARENT_NOT_FOUND: 113012,
  RESOURCE_PARENT_NOT_TOP_LEVEL: 113013,
  RESOURCE_COMMENT_POST_FAILED: 113014,
  RESOURCE_COMMENT_NOT_FOUND: 113020,
  RESOURCE_COMMENT_NOT_AUTHORIZED: 113021,
  RESOURCE_COMMENT_EDIT_FAILED: 113022,
  RESOURCE_COMMENT_DELETE_FAILED: 113023,
  RESOURCE_COMMENTS_LOAD_FAILED: 113030,
  RESOURCE_RATE_LIMITED: 113040,

  // ── Resource interactions: success (domain 13, kind 2) ──────────────
  RESOURCE_COMMENT_POSTED: 213001,
  RESOURCE_COMMENT_UPDATED: 213002,
  RESOURCE_COMMENT_DELETED: 213003,

  // ── Wishes (domain 14) ──────────────────────────────────────────────
  WISHES_REQUIRES_PAID_PLAN: 114001,
  WISHES_EMPTY: 114002,
  WISHES_TEXT_TOO_LONG: 114003,
  WISHES_NOT_FOUND: 114004,
  WISHES_NOT_AUTHORIZED: 114005,
  WISHES_EVENT_MISMATCH: 114006,
  WISHES_INVALID_MEDIA: 114007,
  WISHES_MEDIA_NOT_VIDEO: 114008,
  WISHES_LOAD_FAILED: 114009,
  WISHES_CREATE_FAILED: 114010,
  WISHES_UPDATE_FAILED: 114011,
  WISHES_DELETE_FAILED: 114012,
  WISHES_INVALID_ID: 114013,

  // ── Wishes: success (domain 14, kind 2) ─────────────────────────────
  WISHES_CREATED: 214001,
  WISHES_UPDATED: 214002,
  WISHES_DELETED: 214003,
  WISHES_MARKED_WATCHED: 214004,

  // ── Quiz (domain 15) ────────────────────────────────────────────────
  QUIZ_REQUIRES_PAID_PLAN: 115001,
  QUIZ_NOT_FOUND: 115002,
  QUIZ_NOT_PUBLISHED: 115003,
  QUIZ_QUESTION_NOT_FOUND: 115004,
  QUIZ_QUESTION_LIMIT_REACHED: 115005,
  QUIZ_QUESTION_LOCKED_STRUCTURAL: 115006,
  QUIZ_QUESTION_HAS_RESPONSES: 115007,
  QUIZ_INVALID_OPTION: 115008,
  QUIZ_PUBLISH_NEEDS_QUESTIONS: 115009,
  QUIZ_PUBLISH_NEEDS_CORRECT_ANSWERS: 115010,
  QUIZ_INVALID_STATUS_TRANSITION: 115011,
  QUIZ_FINISH_FIRST: 115012,
  QUIZ_REORDER_MISMATCH: 115013,
  QUIZ_CREATE_FAILED: 115014,
  QUIZ_UPDATE_FAILED: 115015,
  QUIZ_DELETE_FAILED: 115016,
  QUIZ_LOAD_FAILED: 115017,
  QUIZ_ANSWER_FAILED: 115018,
  QUIZ_RATE_LIMITED: 115019,
  QUIZ_NOT_ATTENDEE: 115020,

  // ── Quiz: success (domain 15, kind 2) ───────────────────────────────
  QUIZ_CREATED: 215001,
  QUIZ_UPDATED: 215002,
  QUIZ_PUBLISHED: 215003,
  QUIZ_UNPUBLISHED: 215004,
  QUIZ_QUESTION_ADDED: 215005,
  QUIZ_QUESTION_UPDATED: 215006,
  QUIZ_QUESTION_DELETED: 215007,
  QUIZ_QUESTIONS_REORDERED: 215008,
  QUIZ_ANSWER_SAVED: 215009,

  // ── Seating / table planning (domain 16) ────────────────────────────
  SEATING_EVENT_ID_MISMATCH: 116001,
  SEATING_EVENT_NOT_FOUND: 116002,
  SEATING_TABLE_NOT_FOUND: 116003,
  SEATING_TABLE_HAS_ASSIGNMENTS: 116004,
  SEATING_MEMBER_NOT_IN_RECEPTION_POOL: 116006,
  SEATING_MEMBER_ALREADY_SEATED: 116007,
  SEATING_GUEST_NOT_FOUND: 116008,
  SEATING_NO_MEMBERS_PROVIDED: 116009,
  SEATING_LOAD_FAILED: 116010,
  SEATING_TABLE_AT_CAPACITY: 116011,
  // Guest "Find your seat" lookup is disabled by the couple (or unavailable).
  SEATING_LOOKUP_DISABLED: 116012,

  // ── Seating: success (domain 16, kind 2) ────────────────────────────
  SEATING_TABLE_CREATED: 216001,
  SEATING_TABLE_UPDATED: 216002,
  SEATING_TABLE_DELETED: 216005,
  SEATING_MEMBERS_ASSIGNED: 216006,
  SEATING_MEMBERS_MOVED: 216007,
  SEATING_MEMBERS_UNSEATED: 216008,
  SEATING_PREFERENCES_UPDATED: 216009,

  // ── Organizer / Planning Hub (domain 17, Heirloom feature) ──────────
  ORGANIZER_REQUIRES_PRO_PLAN: 117001,
  ORGANIZER_CATEGORY_NOT_FOUND: 117002,
  ORGANIZER_MILESTONE_NOT_FOUND: 117003,
  ORGANIZER_MILESTONE_READONLY: 117004,
  ORGANIZER_CATEGORY_SLUG_TAKEN: 117005,
  ORGANIZER_VENDOR_NOT_FOUND: 117006,
  ORGANIZER_ACTIVITY_NOT_FOUND: 117007,
  ORGANIZER_ACTIVITY_READONLY: 117008,
  ORGANIZER_TASK_NOT_FOUND: 117009,
  ORGANIZER_PAYMENT_NOT_FOUND: 117010,
  ORGANIZER_DOCUMENT_NOT_FOUND: 117011,
  ORGANIZER_DOCUMENT_UNSUPPORTED_FORMAT: 117012,
  ORGANIZER_DOCUMENT_FILE_TOO_LARGE: 117013,
  ORGANIZER_DOCUMENT_UPLOAD_FAILED: 117014,
  ORGANIZER_DECISION_NOT_FOUND: 117015,
  // Hard-delete guards: only empty, user-created categories can be destroyed.
  ORGANIZER_CATEGORY_NOT_CUSTOM: 117016,
  ORGANIZER_CATEGORY_NOT_EMPTY: 117017,

  // ── Sandbox / "Try the full plan" demo (domain 18) ──────────────────
  SANDBOX_NOT_A_SANDBOX: 118001,
  SANDBOX_UPGRADE_BLOCKED: 118002,
  SANDBOX_EXPIRED: 118003,
  SANDBOX_AT_CAPACITY: 118004,
  SANDBOX_RESET: 218001,

  // ── AI assistant (domain 19, Heirloom feature) ──────────────────────
  AI_REQUIRES_PRO_PLAN: 119001,
  AI_ASSISTANT_UNAVAILABLE: 119002,
  AI_MESSAGE_REQUIRED: 119003,
  AI_MESSAGE_TOO_LONG: 119004,
  AI_CONVERSATION_NOT_FOUND: 119005,
  AI_RATE_LIMITED: 119006,
  AI_DAILY_LIMIT_REACHED: 119007,
  AI_REPLY_IN_PROGRESS: 119008,
  AI_WEEKLY_LIMIT_REACHED: 119009,
  AI_UNAVAILABLE_IN_DEMO: 119010,
  AI_BUDGET_EXHAUSTED: 119011,
  AI_CONVERSATION_RESET: 219001,

  // ── Validation (cross-cutting) ──────────────────────────────────────
  VALIDATION_INVALID_INPUT: 190001,
  VALIDATION_REQUIRED_FIELD: 190002,
  VALIDATION_INVALID_FORMAT: 190003,
  VALIDATION_INVALID_EMAIL: 190010,

  // ── Rate limiting / quotas (cross-cutting) ──────────────────────────
  RATE_LIMIT_EXCEEDED: 191001,

  // ── Generic / internal (cross-cutting) ──────────────────────────────
  GENERIC_INTERNAL_ERROR: 199001,
  GENERIC_NOT_FOUND: 199002,
  GENERIC_UNAUTHORIZED: 199003,
  GENERIC_FORBIDDEN: 199004,
  GENERIC_CONFLICT: 199005,
  // Generic Heirloom-plan gate. Used by every Heirloom-only surface
  // that isn't already covered by a domain-specific code. Surfaces
  // include RSVP, guest list, the editorial invitation page,
  // send-invitations, event sections. NOT Wishes (114001) or Quiz
  // (115001) — those ship with Bouquet too, so their copy differs.
  FEATURE_REQUIRES_PRO_PLAN: 199006,
} as const;

export type MessageCode = (typeof CODES)[keyof typeof CODES];

/**
 * Reverse lookup: numeric code → constant name. Useful for logging.
 */
export const CODE_NAMES: Record<number, string> = Object.fromEntries(
  Object.entries(CODES).map(([name, value]) => [value, name]),
);

export function isKnownCode(value: unknown): value is MessageCode {
  return typeof value === 'number' && value in CODE_NAMES;
}
